import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Activity, RefreshCw, Loader2 } from "lucide-react";

interface Summary {
  totals: { visitors: number; sessions: number; page_views: number; clicks: number; live_now: number };
  daily: { day: string; sessions: number; views: number }[];
  pages: { page: string; views: number; avg_seconds: number }[];
  scroll: { page: string; depth: string; sessions: number }[];
  sections: { section: string; sessions: number; seconds: number }[];
  clicks: { target: string; page: string; clicks: number }[];
  products: { product_id: string; name: string | null; views: number; clicks: number; carts: number; purchases: number; avg_seconds: number }[];
  store_funnel: Record<string, number>;
  booking_funnel: { step: string; sessions: number }[];
  booking_done: number;
  sources: { source: string; sessions: number; orders: number; bookings: number }[];
  devices: { device: string; sessions: number }[];
}

const pct = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)}%` : "—");
const dur = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

function Card({ title, children, hint }: { title: string; children: React.ReactNode; hint?: string }) {
  return (
    <section className="rounded-xl border p-4 space-y-3">
      <div><h2 className="font-semibold">{title}</h2>{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>
      {children}
    </section>
  );
}

function Bars({ rows, total }: { rows: { label: string; value: number; extra?: string }[]; total?: number }) {
  const max = total ?? Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <p className="text-sm text-muted-foreground">Sem dados ainda.</p>;
  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.label} className="text-sm">
          <div className="flex justify-between gap-2"><span className="truncate">{r.label}</span><span className="text-muted-foreground shrink-0">{r.value}{r.extra ? ` · ${r.extra}` : ""}</span></div>
          <div className="h-1.5 rounded bg-muted"><div className="h-1.5 rounded bg-primary" style={{ width: `${Math.min(100, (r.value / max) * 100)}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export default function Metrics() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    const { data, error } = await (supabase as any).rpc("analytics_summary", { _days: Number(days) });
    if (error) setError(error.message); else setData(data as Summary);
    setLoading(false);
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const sf = data?.store_funnel || {};
  const storeSteps: [string, number][] = [["Visitaram a loja", sf.store_visit || 0], ["Viram um produto", sf.product_view || 0], ["Adicionaram ao carrinho", sf.add_to_cart || 0], ["Iniciaram o pedido", sf.checkout_start || 0], ["Compraram", sf.purchase || 0]];

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Activity className="w-6 h-6" /> Métricas do site</h1>
          <p className="text-sm text-muted-foreground">Apenas visitantes que aceitaram os cookies são contabilizados. Não é guardado o IP.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{["7", "30", "90", "180"].map((d) => <SelectItem key={d} value={d}>Últimos {d} dias</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {loading && <Loader2 className="w-6 h-6 animate-spin" />}
      {error && <p className="text-destructive text-sm">Erro: {error}. Já aplicou a migração de analítica no Supabase?</p>}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[["Visitantes", data.totals.visitors], ["Sessões", data.totals.sessions], ["Páginas vistas", data.totals.page_views], ["Cliques", data.totals.clicks], ["Online agora", data.totals.live_now]].map(([l, v]) => (
              <div key={l as string} className="rounded-xl border p-4"><div className="text-2xl font-bold">{v as number}</div><div className="text-xs text-muted-foreground">{l}</div></div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card title="Funil da loja" hint="Sessões únicas em cada etapa e % em relação à etapa anterior">
              <Bars rows={storeSteps.map(([label, value], i) => ({ label, value, extra: i ? pct(value, storeSteps[i - 1][1]) : undefined }))} />
            </Card>
            <Card title="Funil de reservas" hint="Em que passo os clientes desistem">
              <Bars rows={[...data.booking_funnel.map((b) => ({ label: b.step, value: b.sessions })), { label: "Reserva concluída", value: data.booking_done }]} />
            </Card>

            <Card title="Produtos" hint="Vistas, cliques, carrinho e compras por produto">
              {data.products.length === 0 ? <p className="text-sm text-muted-foreground">Sem dados ainda.</p> : (
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead><tr className="text-left text-muted-foreground"><th>Produto</th><th>Vistas</th><th>Cliques</th><th>Carrinho</th><th>Compras</th><th>Tempo</th></tr></thead>
                  <tbody>{data.products.map((p) => (
                    <tr key={p.product_id} className="border-t"><td className="py-1 pr-2 max-w-[180px] truncate">{p.name || p.product_id.slice(0, 8)}</td><td>{p.views}</td><td>{p.clicks}</td><td>{p.carts}</td><td>{p.purchases}</td><td>{dur(p.avg_seconds)}</td></tr>
                  ))}</tbody></table></div>
              )}
            </Card>
            <Card title="Origem do tráfego" hint="De onde vêm e quais origens geram vendas e reservas">
              {data.sources.length === 0 ? <p className="text-sm text-muted-foreground">Sem dados ainda.</p> : (
                <table className="w-full text-sm"><thead><tr className="text-left text-muted-foreground"><th>Origem</th><th>Sessões</th><th>Encomendas</th><th>Reservas</th></tr></thead>
                  <tbody>{data.sources.map((s) => <tr key={s.source} className="border-t"><td className="py-1">{s.source}</td><td>{s.sessions}</td><td>{s.orders}</td><td>{s.bookings}</td></tr>)}</tbody></table>
              )}
            </Card>

            <Card title="Páginas mais vistas" hint="Tempo médio de permanência por página">
              <Bars rows={data.pages.map((p) => ({ label: p.page, value: p.views, extra: dur(p.avg_seconds) }))} />
            </Card>
            <Card title="Secções onde ficam mais tempo" hint="Tempo total visível no ecrã">
              <Bars rows={data.sections.map((s) => ({ label: s.section, value: s.seconds, extra: `${s.sessions} sessões` }))} />
            </Card>

            <Card title="Onde mais clicam">
              <Bars rows={data.clicks.map((c) => ({ label: `${c.target} (${c.page})`, value: c.clicks }))} />
            </Card>
            <Card title="Profundidade de scroll" hint="Sessões que chegaram a 25/50/75/100% de cada página">
              <Bars rows={data.scroll.filter((s) => data.pages.slice(0, 5).some((p) => p.page === s.page)).map((s) => ({ label: `${s.page} · ${s.depth}%`, value: s.sessions }))} />
            </Card>

            <Card title="Dispositivos">
              <Bars rows={data.devices.map((d) => ({ label: d.device, value: d.sessions }))} />
            </Card>
            <Card title="Sessões por dia">
              <Bars rows={data.daily.slice(-14).map((d) => ({ label: d.day, value: d.sessions, extra: `${d.views} vistas` }))} />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
